"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { IconUserCircle, IconLogout, IconId } from "@tabler/icons-react";
import styles from "./Navbar.module.css";

/**
 * Legge l'email dell'utente dal cookie `user_email`.
 * Si usa il cookie lato client perché non c'è un UserContext globale:
 * l'autenticazione è gestita dal proxy/middleware server, non da React state.
 */
function getUserEmail(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(^|;\s*)user_email=([^;]+)/);
  return m ? decodeURIComponent(m[2]) : null;
}

type NavbarProps = {
  /** Se false nasconde i link di navigazione e i bottoni auth (usato nelle pagine di reset/forgot). */
  showActions?: boolean;
};

/** Navbar fissa con supporto scroll-blur, dropdown profilo e menu mobile. */
export default function Navbar({ showActions = true }: NavbarProps) {
  const router = useRouter();
  const headerRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Inizializzato a null sia su server che su client: evita l'hydration mismatch.
  // Il cookie è disponibile solo nel browser, quindi viene letto dopo l'idratazione nell'effect.
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // classList invece di stato React: aggiornare state ad ogni evento scroll
    // causerebbe re-render continui; manipolare il DOM direttamente è O(1) e non blocca.
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.scrollY > 10) {
          headerRef.current.classList.add(styles.scrolled);
        } else {
          headerRef.current.classList.remove(styles.scrolled);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Il cookie è disponibile solo nel browser, non durante il render SSR.
  // Inizializzare lo state a null garantisce che server e client producano lo stesso
  // HTML iniziale (nessun hydration mismatch). L'effect gira solo sul client,
  // dopo l'idratazione, e aggiorna lo state con il valore reale del cookie.
  useEffect(() => {
    setUserEmail(getUserEmail());
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const close = () => setMenuOpen(false);

  const handleLogout = async () => {
    await fetch("/logout", { method: "POST" });
    setUserEmail(null);
    setDropdownOpen(false);
    close();
    router.replace("/");
  };

  const isLoggedIn = !!userEmail;

  // Icona profilo nascosta dalla headbar e dal menu mobile: senza login non c'e'
  // una sessione utente da mostrare. Il codice resta sotto, inattivo.
  // Per riattivarla: rimettere showProfile a true.
  const showProfile = false;

  return (
    <>
      <header ref={headerRef} className={styles.header}>
        <nav className={`${styles.nav} container`}>
          <Link href="/" className={styles.logo}>
            <Image
              src="/logo_atlas.png"
              alt="Atlas"
              width={34}
              height={34}
              className={styles.logoAtlasImg}
            />
            <div className={styles.logoText}>
              <span className={styles.logoPowered}>Powered by</span>
              <span style={{marginLeft: "1px"}} className={styles.logoBrand}>
                <Image
                  src="/logotext_fast_oneline_light.png"
                  alt="FAST Computing"
                  width={270}
                  height={45}
                  style={{ objectFit: "contain" }}
                />
              </span>
            </div>
          </Link>

          {showActions && (
            <div className={styles.navActions}>
              <div className={styles.navLinks}>
                <Link href="/applications">Applications</Link>
                <Link href="/privacy">Privacy</Link>
              </div>
              {showProfile && isLoggedIn ? (
                <div className={styles.navProfile} ref={dropdownRef}>
                  <button type="button"
                    className={styles.navProfileBtn}
                    onClick={() => setDropdownOpen((o) => !o)}
                    aria-label="Profilo utente"
                  >
                    <IconUserCircle size={32} stroke={1.5} />
                  </button>

                  {dropdownOpen && (
                    <div className={styles.navDropdown}>
                      <span className={styles.navDropdownEmail}>{userEmail}</span>
                      <Link
                        href="/profile"
                        className={styles.navDropdownItem}
                        onClick={() => setDropdownOpen(false)}
                      >
                        <IconId size={16} />
                        Profilo
                      </Link>
                      <button type="button"
                        className={`${styles.navDropdownItem} ${styles.navDropdownItemDanger}`}
                        onClick={handleLogout}
                      >
                        <IconLogout size={16} />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // Login/Sign Up disabilitati: il sito e' accessibile senza account,
                // il bottone "Try for free" della home porta direttamente a /applications.
                // Per riattivarli: rimuovere `null` e togliere i commenti dal blocco sotto.
                null
                /*
                <>
                  <Link href="/account/login">
                    <button type="button" className="btn btn-secondary">Login</button>
                  </Link>
                  <Link href="/account/signup">
                    <button type="button" className="btn btn-primary">Sign Up</button>
                  </Link>
                </>
                */
              )}
            </div>
          )}

          <button type="button" className={styles.menuToggle} onClick={() => setMenuOpen(true)}>
            <span />
            <span />
            <span />
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <div className={`${styles.mobileMenu}${menuOpen ? ` ${styles.active}` : ""}`}>
        <button type="button" className={styles.menuClose} onClick={close}>
          X
        </button>
        <nav className={styles.mobileLinks}>
          <Link href="/applications" onClick={close}>
            Applications
          </Link>
          <Link href="/#contact" onClick={close}>
            Contact
          </Link>
          <Link href="/privacy" onClick={close}>
            Privacy
          </Link>
        </nav>

        {showProfile && (
          <div className={styles.mobileProfile}>
            <div className={styles.profileAvatar}>
              <IconUserCircle size={36} stroke={1.5} />
            </div>
            <div>
              {isLoggedIn ? (
                <>
                  <strong>Account</strong>
                  <small>{userEmail}</small>
                </>
              ) : (
                <>
                  <strong>Guest User</strong>
                  <small>Not logged in</small>
                </>
              )}
            </div>
          </div>
        )}

        <div className={styles.mobileActions}>
          {isLoggedIn ? (
            <>
              <Link href="/profile" onClick={close}>
                <button type="button" className="btn btn-secondary">Profilo</button>
              </Link>
              <button type="button" className="btn btn-primary" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            // Login/Sign Up disabilitati anche nel menu mobile (vedi nota sopra).
            null
            /*
            <>
              <Link href="/account/login" onClick={close}>
                <button type="button" className="btn btn-secondary">Login</button>
              </Link>
              <Link href="/account/signup" onClick={close}>
                <button type="button" className="btn btn-primary">Sign Up</button>
              </Link>
            </>
            */
          )}
        </div>
      </div>

      <div
        className={`${styles.menuOverlay}${menuOpen ? ` ${styles.active}` : ""}`}
        onClick={close}
      />
    </>
  );
}
